import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import type { Room } from 'colyseus.js';
import type { PokerState } from '../lib/pokerSchema';
import type { TableConfig } from '../lib/gameClient';
import { saveTableSession, removeTableSession } from '../lib/tableSession';

/* ─── Snapshot ─── */

export interface TableSnapshot {
  phase: string;
  pot: number;        // cents
  isMyTurn: boolean;
  myStack: number;    // cents; -1 = spectating/not seated
  playerCount: number;
  turnEndsAt: number; // ms timestamp
  holeCards: string[]; // e.g. ["Ah","Kd"] — empty between hands
}

/* ─── Entry ─── */

export interface TableEntry {
  id: string;
  config: TableConfig;
  room: Room<PokerState>;
  snapshot: TableSnapshot;
  userId: string;
}

/* ─── Context value ─── */

export type AppView = 'lobby' | 'table';

export interface MultiTableContextValue {
  tables: Map<string, TableEntry>;
  activeTableId: string | null;
  view: AppView;
  registerTable: (
    id: string,
    config: TableConfig,
    room: Room<PokerState>,
    userId: string,
    opts?: { activate?: boolean },
  ) => void;
  unregisterTable: (tableId: string) => void;
  switchToTable: (tableId: string) => void;
  goToLobby: () => void;
}

const MultiTableContext = createContext<MultiTableContextValue | null>(null);

export function useMultiTable(): MultiTableContextValue {
  const ctx = useContext(MultiTableContext);
  if (!ctx) throw new Error('useMultiTable must be used inside MultiTableProvider');
  return ctx;
}

/* ─── Snapshot computation ─── */

type RoomLike = Room<PokerState> & {
  state: {
    phase?: unknown;
    pot?: unknown;
    currentTurnSeat?: unknown;
    turnEndsAt?: unknown;
    seats?: { values?: () => Iterable<{ userId?: string; seatIndex?: number; stack?: number; status?: string }> };
  };
  reconnectionToken?: string;
};

function computeSnapshot(room: Room<PokerState>, userId: string): TableSnapshot {
  const r = room as unknown as RoomLike;
  const s = r.state;
  const seats = Array.from(s.seats?.values?.() ?? []);
  const mySeat = seats.find((seat) => seat.userId === userId);
  const mySeatIndex = mySeat?.seatIndex ?? -1;
  const phase = (s.phase as string) ?? 'waiting';
  const inHandPhase = ['preflop', 'flop', 'turn', 'river'].includes(phase);
  const isMyTurn = inHandPhase && (s.currentTurnSeat as number) === mySeatIndex && mySeatIndex >= 0;
  const activePlayers = seats.filter((seat) => seat.status !== 'empty').length;
  return {
    phase,
    pot: Number(s.pot) || 0,
    isMyTurn,
    myStack: mySeat ? (mySeat.stack ?? 0) : -1,
    playerCount: activePlayers,
    turnEndsAt: Number(s.turnEndsAt ?? 0),
    holeCards: [], // populated separately via room.onMessage('holeCards')
  };
}

/* ─── Provider ─── */

export function MultiTableProvider({ children }: { children: React.ReactNode }) {
  // tablesRef is always authoritative; state copy drives renders
  const tablesRef = useRef<Map<string, TableEntry>>(new Map());
  const [tables, setTablesState] = useState<Map<string, TableEntry>>(new Map());

  const activeTableIdRef = useRef<string | null>(null);
  const [activeTableId, setActiveTableIdState] = useState<string | null>(null);

  const [view, setView] = useState<AppView>('lobby');

  // One cleanup fn per table (removes onStateChange listener)
  const cleanupRefs = useRef<Map<string, () => void>>(new Map());

  /* ─── Helpers that keep ref + state in sync ─── */

  const pushTables = useCallback(() => {
    setTablesState(new Map(tablesRef.current));
  }, []);

  const setActiveTableId = useCallback((id: string | null) => {
    activeTableIdRef.current = id;
    setActiveTableIdState(id);
  }, []);

  /* ─── Internal unregister — safe to call from other closures ─── */

  const unregisterTableInternal = useCallback(
    (tableId: string, callLeave: boolean) => {
      // Remove state listener
      cleanupRefs.current.get(tableId)?.();
      cleanupRefs.current.delete(tableId);

      // Leave room
      if (callLeave) {
        const entry = tablesRef.current.get(tableId);
        if (entry?.room) {
          try {
            (entry.room as unknown as { leave: (consented: boolean) => void }).leave(true);
          } catch {
            // ignore
          }
        }
      }

      // Remove persisted session
      removeTableSession(tableId);

      // Update ref + state
      tablesRef.current.delete(tableId);
      pushTables();

      // Navigate if this was the active table
      if (activeTableIdRef.current === tableId) {
        const remaining = [...tablesRef.current.keys()];
        if (remaining.length > 0) {
          const nextId = remaining[0];
          setActiveTableId(nextId);
          setView('table');
          history.pushState(null, '', `/table/${nextId}`);
        } else {
          setActiveTableId(null);
          setView('lobby');
          history.pushState(null, '', '/');
        }
      }
    },
    [pushTables, setActiveTableId],
  );

  /* ─── Register ─── */

  const registerTable = useCallback(
    (
      tableId: string,
      config: TableConfig,
      room: Room<PokerState>,
      userId: string,
      opts?: { activate?: boolean },
    ) => {
      // Clean up any stale listener for this tableId
      cleanupRefs.current.get(tableId)?.();

      const entry: TableEntry = {
        id: tableId,
        config,
        room,
        snapshot: computeSnapshot(room, userId),
        userId,
      };
      tablesRef.current.set(tableId, entry);
      pushTables();

      // Track previous turn state to detect transitions
      let prevIsMyTurn = false;

      const stateHandler = () => {
        const snap = computeSnapshot(room, userId);

        // When it becomes our turn on a background table, update the document title
        if (snap.isMyTurn && !prevIsMyTurn && activeTableIdRef.current !== tableId) {
          document.title = '⚡ Your Turn! — NoCutPoker';
          setTimeout(() => {
            if (document.title.startsWith('⚡')) document.title = 'NoCutPoker';
          }, 10000);
        }
        prevIsMyTurn = snap.isMyTurn;

        const existing = tablesRef.current.get(tableId);
        if (!existing) return;

        // Preserve hole cards across state patches.
        // Clear them when a new hand starts (countdown/preflop) or ends (waiting).
        const prevPhase = existing.snapshot.phase;
        const handReset =
          (snap.phase === 'waiting' || snap.phase === 'countdown') &&
          prevPhase !== 'waiting' && prevPhase !== 'countdown';
        snap.holeCards = handReset ? [] : existing.snapshot.holeCards;

        tablesRef.current.set(tableId, { ...existing, snapshot: snap });
        pushTables();

        // Keep session token fresh
        const token = (room as unknown as { reconnectionToken?: string }).reconnectionToken;
        if (token) saveTableSession({ reconnectionToken: token, tableId, config, userId });
      };

      room.onStateChange(stateHandler);

      // Capture private hole-card messages for the dock preview
      room.onMessage('holeCards', (msg: { cards: string[] }) => {
        const existing = tablesRef.current.get(tableId);
        if (!existing) return;
        tablesRef.current.set(tableId, {
          ...existing,
          snapshot: { ...existing.snapshot, holeCards: msg.cards },
        });
        pushTables();
      });

      // Handle server-initiated disconnect (e.g. table timeout)
      const leaveHandler = (code: number) => {
        // Code 4000 = we called room.leave(true) — unregisterTableInternal already handles it
        if (code === 4000) return;
        if (tablesRef.current.has(tableId)) {
          unregisterTableInternal(tableId, false);
        }
      };
      room.onLeave(leaveHandler);

      cleanupRefs.current.set(tableId, () => {
        room.onStateChange.remove(stateHandler);
        // room.onLeave has no .remove in colyseus.js; that's acceptable — the
        // handler exits early via the guard check above if called after unregister
      });

      // Switch to this table unless caller opts out
      if (opts?.activate !== false) {
        setActiveTableId(tableId);
        setView('table');
        history.pushState(null, '', `/table/${tableId}`);
      }
    },
    [pushTables, setActiveTableId, unregisterTableInternal],
  );

  /* ─── Public unregister ─── */

  const unregisterTable = useCallback(
    (tableId: string) => unregisterTableInternal(tableId, true),
    [unregisterTableInternal],
  );

  /* ─── Navigation helpers ─── */

  const switchToTable = useCallback(
    (tableId: string) => {
      setActiveTableId(tableId);
      setView('table');
      history.pushState(null, '', `/table/${tableId}`);
      document.title = 'NoCutPoker';
    },
    [setActiveTableId],
  );

  const goToLobby = useCallback(() => {
    setView('lobby');
    history.pushState(null, '', '/');
  }, []);

  /* ─── Browser back/forward ─── */

  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname;
      const m = path.match(/^\/table\/(.+)$/);
      if (m && tablesRef.current.has(m[1])) {
        setActiveTableId(m[1]);
        setView('table');
      } else {
        setView('lobby');
        if (!m) setActiveTableId(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [setActiveTableId]);

  const value: MultiTableContextValue = {
    tables,
    activeTableId,
    view,
    registerTable,
    unregisterTable,
    switchToTable,
    goToLobby,
  };

  return <MultiTableContext.Provider value={value}>{children}</MultiTableContext.Provider>;
}
